/* eslint-disable @typescript-eslint/no-explicit-any */
import { API_CONFIG, getApiUrl } from "@/config/apiConfig";
import { Project } from "../types/project";
import { getAuthHeaders, getFileHeaders } from "./authApi";
import { handleResponse } from "./apiHandler";

const generateSlug = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

const STORAGE_KEY = "pagebuilder_projects";

const initializeStorage = async (): Promise<void> => {
  const existing = localStorage.getItem(STORAGE_KEY);
  if (!existing) {
    try {
      const response = await fetch("/data/projects.json");
      const dummyProjects = await response.json();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dummyProjects));
    } catch (error) {
      console.error("Failed to load dummy projects:", error);
      localStorage.setItem(STORAGE_KEY, JSON.stringify([]));
    }
  }
};

const getStoredProjects = (): any[] => {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored ? JSON.parse(stored) : [];
};

const saveStoredProjects = (projects: any[]): void => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(projects));
};

export const fetchProjectqr = async (
  id: any,
  page?: any,
  pageSize?: any,
): Promise<Project[]> => {
  const endpoint = API_CONFIG.endpoints.project.getprojectqr.replace(
    ":userId",
    id,
  );

  const url = `${getApiUrl(endpoint)}?page=${page}&pageSize=${pageSize}`;

  const response = await fetch(url, {
    method: "get",
    headers: getAuthHeaders(),
  });

  return (await handleResponse(response, "Fetch Project QR")) as any;
};

export const fetchProjects = async (
  id: string,
  page?: number,
  pageSize?: number,
  search?: string,
  type?: "all" | "single" | "multi",
): Promise<Project[]> => {
  const endpoint = API_CONFIG.endpoints.project.get.replace(":userId", id);

  const params = new URLSearchParams();

  if (page) params.append("page", page.toString());
  if (pageSize) params.append("pageSize", pageSize.toString());

  if (search?.trim()) {
    params.append("search", search.trim());
  }

  // Don't send type when "all" is selected
  if (type && type !== "all") {
    params.append("type", type);
  }

  const url = `${getApiUrl(endpoint)}?${params.toString()}`;

  const response = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  return (await handleResponse(response, "Fetch Projects")) as any;
};

export const fetchProject = async (id: string): Promise<Project> => {
  const endpoint = API_CONFIG.endpoints.project.details.replace(":id", id);
  const url = `${getApiUrl(endpoint)}`;

  const response = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  const result = await handleResponse(response, "Fetch Project Details");
  const project = result.value;

  if (!project) {
    throw new Error("Project not found");
  }

  // Normalize banner settings and provide sensible defaults
  const bannerRaw = project.banner;
  let banner = bannerRaw || {
    type: "color" as const,
    color: "#3b82f6",
    videoLoop: true,
    videoMuted: true,
  };

  if (banner.type === "gradient") {
    banner = {
      type: "gradient",
      gradientStart:
        banner.gradientStart || project.colorScheme?.primary || "#3b82f6",
      gradientEnd:
        banner.gradientEnd || project.colorScheme?.secondary || "#8b5cf6",
      gradientDirection: banner.gradientDirection || "to bottom",
    };
  }

  const normalizedProject = {
    ...project,
    banner,
    showBrandName:
      project.showBrandName !== undefined ? project.showBrandName : false,
    brandNameColor: project.brandNameColor || "#ffffff",
    navbarBackground: project.navbarBackground || "transparent",
    logoAlignment: project.logoAlignment || "left",
    offcanvasTitle: project.offcanvasTitle || "Explore More",
    offcanvasTitlecolor: project.offcanvasTitlecolor || "#000000",
    offcanvasSubtitlecolor: project.offcanvasSubtitlecolor || "#000000",
    offcanvasBgColor: project.offcanvasBgColor || "#ffffff",
    themeTextColor: project.themeTextColor || "#000000",
    themeFont: project.themeFont || "Inter",
    buttonStyle: project.buttonStyle || {
      style: "default",
      defaultColor: "#3b82f6",
      hoverColor: "#2563eb",
      fillColor: "#3b82f6",
      borderColor: "#3b82f6",
      isTransparent: false,
      textColor: "#ffffff",
      hoverTextColor: "#ffffff",
    },
    messageColors: project.messageColors || {
      success: "#10b981",
      error: "#ef4444",
      warning: "#f59e0b",
      info: "#06b6d4",
    },
    status: (project.status === "active" || !project.status
      ? "publish"
      : project.status === "inactive"
        ? "draft"
        : project.status) as "publish" | "draft",
    createdAt: new Date(project.createdAt),
    updatedAt: new Date(project.updatedAt),
  };

  return normalizedProject;
};

export const fetchProjectBySlug = async (slug: string): Promise<Project> => {
  const endpoint = API_CONFIG.endpoints.project.getslug.replace(":slug", slug);
  const url = `${getApiUrl(endpoint)}`;

  const response = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  const result = await handleResponse(response, "Fetch Project By Slug");
  const project = { ...result.value.project, pages: result.value.pages ,showWatermark:result.value.showWatermark };
   

  if (!project) {
    throw new Error("Project not found");
  }

  // Normalize banner settings and provide sensible defaults
  const bannerRaw = project.banner;
  let banner = bannerRaw || {
    type: "color" as const,
    color: "#3b82f6",
    videoLoop: true,
    videoMuted: true,
  };

  if (banner.type === "gradient") {
    banner = {
      type: "gradient",
      gradientStart:
        banner.gradientStart || project.colorScheme?.primary || "#3b82f6",
      gradientEnd:
        banner.gradientEnd || project.colorScheme?.secondary || "#8b5cf6",
      gradientDirection: banner.gradientDirection || "to bottom",
    };
  }

  const normalizedProject = {
    ...project,
    banner,
    
    showBrandName:
      project.showBrandName !== undefined ? project.showBrandName : true,
    brandNameColor: project.brandNameColor || "#ffffff",
    navbarBackground: project.navbarBackground || "transparent",
    logoAlignment: project.logoAlignment || "left",
    offcanvasTitle: project.offcanvasTitle || "Explore More",
    offcanvasBgColor: project.offcanvasBgColor || "#ffffff",
    themeTextColor: project.themeTextColor || "#000000",
    themeFont: project.themeFont || "Inter",
    buttonStyle: project.buttonStyle || {
      style: "default",
      defaultColor: "#3b82f6",
      hoverColor: "#2563eb",
      fillColor: "#3b82f6",
      borderColor: "#3b82f6",
      isTransparent: false,
      textColor: "#ffffff",
      hoverTextColor: "#ffffff",
    },
    messageColors: project.messageColors || {
      success: "#10b981",
      error: "#ef4444",
      warning: "#f59e0b",
      info: "#06b6d4",
    },
    status: (project.status === "active" || !project.status
      ? "publish"
      : project.status === "inactive"
        ? "draft"
        : project.status) as "publish" | "draft",
    createdAt: new Date(project.createdAt),
    updatedAt: new Date(project.updatedAt),
  };

  return normalizedProject;
};

export const fetchTemplateBySlug = async (id: string): Promise<Project> => {
  const endpoint = API_CONFIG.endpoints.template.preview.replace(":id", id);
  const url = `${getApiUrl(endpoint)}`;

  const response = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  const result = await handleResponse(response, "Fetch Project By Slug");
  // const project = { ...result.value.template, pages: result.value.pages };

  const project = {
    ...result.value.template,
    pages: result.value.pages, 
    type: result.value.template.template_type,
    name: result.value.template.title,
  };

  if (!project) {
    throw new Error("Project not found");
  }

  // Normalize banner settings and provide sensible defaults
  const bannerRaw = project.banner;
  let banner = bannerRaw || {
    type: "color" as const,
    color: "#3b82f6",
    videoLoop: true,
    videoMuted: true,
  };

  if (banner.type === "gradient") {
    banner = {
      type: "gradient",
      gradientStart:
        banner.gradientStart || project.colorScheme?.primary || "#3b82f6",
      gradientEnd:
        banner.gradientEnd || project.colorScheme?.secondary || "#8b5cf6",
      gradientDirection: banner.gradientDirection || "to bottom",
    };
  }

  const normalizedProject = {
    ...project,
    banner,
    showBrandName:
      project.showBrandName !== undefined ? project.showBrandName : true,
    brandNameColor: project.brandNameColor || "#ffffff",
    navbarBackground: project.navbarBackground || "transparent",
    logoAlignment: project.logoAlignment || "left",
    offcanvasTitle: project.offcanvasTitle || "Explore More",
    offcanvasBgColor: project.offcanvasBgColor || "#ffffff",
    themeTextColor: project.themeTextColor || "#000000",
    themeFont: project.themeFont || "Inter",
    buttonStyle: project.buttonStyle || {
      style: "default",
      defaultColor: "#3b82f6",
      hoverColor: "#2563eb",
      fillColor: "#3b82f6",
      borderColor: "#3b82f6",
      isTransparent: false,
      textColor: "#ffffff",
      hoverTextColor: "#ffffff",
    },
    
    messageColors: project.messageColors || {
      success: "#10b981",
      error: "#ef4444",
      warning: "#f59e0b",
      info: "#06b6d4",
    },
    status: (project.status === "active" || !project.status
      ? "publish"
      : project.status === "inactive"
        ? "draft"
        : project.status) as "publish" | "draft",
    createdAt: new Date(project.createdAt),
    updatedAt: new Date(project.updatedAt),
  };

  return normalizedProject;
};

export const createProject = async (project: Project): Promise<Project> => {
  const endpoint = API_CONFIG.endpoints.project.create;
  const url = `${getApiUrl(endpoint)}`;

  const newProject: Project = { ...project };

  const response = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(newProject),
  });

  return (await handleResponse(response, "Create Project")) as any;
};

export const updateProject = async (
  id: string,
  updates: Partial<Project>,
): Promise<Project> => {
  const cleanProject = { ...updates };
  delete cleanProject.created_by;

  const endpoint = API_CONFIG.endpoints.project.update.replace(":id", id);
  const url = `${getApiUrl(endpoint)}`;

  const response = await fetch(url, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify(cleanProject),
  });

  const result = await handleResponse(response, "Update Project");
  const updatedProject = result.value;

  if (!updatedProject) {
    throw new Error("Project not found");
  }

  return updatedProject;
};

export const updateStatus = async (id: string): Promise<Project> => {
  const endpoint = API_CONFIG.endpoints.project.statusupdate.replace(":id", id);
  const url = `${getApiUrl(endpoint)}`;

  const response = await fetch(url, {
    method: "PUT",
    headers: getAuthHeaders(),
  });

  const result = await handleResponse(response, "Update Project Status");
  const updatedProject = result.value;

  if (!updatedProject) {
    throw new Error("Project not found");
  }

  return updatedProject;
};

export const deleteProject = async (id: string): Promise<void> => {
  const endpoint = API_CONFIG.endpoints.project.delete.replace(":id", id);
  const url = `${getApiUrl(endpoint)}`;

  const response = await fetch(url, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });

  await handleResponse(response, "Delete Project");
};
